import React, { useState } from 'react';
import { useQuery } from 'react-query';
import { 
  Bot, 
  ExternalLink, 
  Copy, 
  CheckCircle, 
  AlertCircle,
  Users,
  Settings
} from 'lucide-react';
import { discordAPI } from '../services/api';
import toast from 'react-hot-toast';

const BotInvite = ({ 
  variant = 'default', // 'default', 'compact', 'hero'
  showStatus = true,
  showPermissions = true,
  className = ''
}) => {
  const [copied, setCopied] = useState(false);

  // Fetch bot invite link
  const { data: inviteData, isLoading, error } = useQuery(
    'botInvite',
    () => discordAPI.getInviteLink().then(res => res.data),
    {
      staleTime: 5 * 60 * 1000, // 5 minutes
      onError: (err) => {
        console.error('Bot invite error:', err);
        toast.error('Failed to load bot invite link');
      }
    }
  );

  const handleInviteClick = () => {
    if (inviteData?.inviteUrl) {
      window.open(inviteData.inviteUrl, '_blank');
    }
  };

  const handleCopyLink = async (e) => {
    e.stopPropagation();
    if (inviteData?.inviteUrl) {
      try {
        await navigator.clipboard.writeText(inviteData.inviteUrl);
        setCopied(true);
        toast.success('Invite link copied to clipboard!');
        setTimeout(() => setCopied(false), 2000);
      } catch (err) {
        toast.error('Failed to copy link');
      }
    }
  };

  if (isLoading) {
    return (
      <div className={`animate-pulse ${className}`}>
        <div className="h-12 bg-discord-dark rounded-lg"></div>
      </div>
    );
  }

  if (error || !inviteData) {
    return (
      <div className={`bg-red-500/10 border border-red-500/20 rounded-lg p-4 ${className}`}>
        <div className="flex items-center space-x-2 text-red-400">
          <AlertCircle className="h-5 w-5" />
          <span>Failed to load bot invite link</span>
        </div>
      </div>
    );
  }

  const permissions = inviteData.permissions || [];

  if (variant === 'hero') {
    return (
      <div className={`space-y-4 ${className}`}>
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <button
            onClick={handleInviteClick}
            className="btn btn-primary bg-discord-blurple hover:bg-discord-blurple/90 text-white text-lg px-8 py-3 flex items-center justify-center"
          >
            <Bot className="mr-2 h-5 w-5" />
            Add Bot to Server
            <ExternalLink className="ml-2 h-5 w-5" />
          </button>
          
          <button
            onClick={handleCopyLink}
            className="btn btn-secondary border-discord-blurple text-discord-blurple hover:bg-discord-blurple/10 text-lg px-8 py-3 flex items-center justify-center"
          >
            {copied ? (
              <>
                <CheckCircle className="mr-2 h-5 w-5" />
                Copied!
              </>
            ) : (
              <>
                <Copy className="mr-2 h-5 w-5" />
                Copy Link
              </>
            )}
          </button>
        </div>

        {showPermissions && permissions.length > 0 && (
          <div className="text-center">
            <p className="text-discord-light text-sm mb-2">
              Required permissions:
            </p>
            <div className="flex flex-wrap justify-center gap-2">
              {permissions.map((permission, index) => (
                <span
                  key={index}
                  className="badge badge-primary bg-discord-blurple/20 text-discord-blurple border-discord-blurple/30"
                >
                  {permission}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }

  if (variant === 'compact') {
    return (
      <div className={`bg-discord-darker p-4 rounded-lg border border-discord-dark ${className}`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-discord-blurple/20 rounded-full">
              <Bot className="h-5 w-5 text-discord-blurple" />
            </div>
            <div>
              <h3 className="text-discord-lightest font-medium">Add Bot to Server</h3>
              <p className="text-discord-light text-sm">Click to invite the bot</p>
            </div>
          </div>
          <button
            onClick={handleInviteClick}
            className="btn btn-primary btn-sm"
          >
            Invite
            <ExternalLink className="ml-1 h-4 w-4" />
          </button>
        </div>
      </div>
    );
  }

  // Default variant
  return (
    <div className={`bg-discord-darker p-6 rounded-lg border border-discord-dark ${className}`}>
      <div className="flex items-center space-x-3 mb-4">
        <div className="p-3 bg-discord-blurple/20 rounded-full">
          <Bot className="h-6 w-6 text-discord-blurple" />
        </div>
        <div>
          <h3 className="text-lg font-semibold text-discord-lightest">
            Add Bot to Your Server
          </h3>
          <p className="text-discord-light text-sm">
            Invite the bot to start playing games with your community
          </p>
        </div>
      </div>

      <div className="space-y-4">
        <div className="flex flex-col sm:flex-row gap-3">
          <button
            onClick={handleInviteClick}
            className="btn btn-primary flex-1 flex items-center justify-center"
          >
            <Bot className="mr-2 h-5 w-5" />
            Add to Discord
            <ExternalLink className="ml-2 h-5 w-5" />
          </button>
          
          <button
            onClick={handleCopyLink}
            className="btn btn-secondary flex items-center justify-center"
          >
            {copied ? (
              <>
                <CheckCircle className="mr-2 h-5 w-5" />
                Copied!
              </>
            ) : (
              <>
                <Copy className="mr-2 h-5 w-5" />
                Copy Link
              </>
            )}
          </button>
        </div>

        {showPermissions && permissions.length > 0 && (
          <div>
            <p className="text-discord-light text-sm mb-2">
              Required permissions:
            </p>
            <div className="flex flex-wrap gap-2">
              {permissions.map((permission, index) => (
                <span
                  key={index}
                  className="badge badge-primary bg-discord-blurple/20 text-discord-blurple border-discord-blurple/30"
                >
                  {permission}
                </span>
              ))}
            </div>
          </div>
        )}

        {showStatus && (
          <div className="pt-4 border-t border-discord-dark">
            <div className="flex items-center space-x-2 text-discord-light text-sm">
              <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
              <span>Bot is online and ready to play!</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BotInvite;
